import type { OverlayRef } from '@angular/cdk/overlay';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import type { ComponentRef, ElementRef, Type } from '@angular/core';
import { Injectable } from '@angular/core';

import { fromEvent as observableFromEvent, queueScheduler, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import type { RendererCallbackFn, WalkthroughStepOrigin } from '@core/walkthrough-types';

import type { Renderer, Step, WalkthroughComponentT } from '../types';

@Injectable({ providedIn: 'root' })
export class WalkthroughComponentRenderer implements Renderer {
  private overlayRef: OverlayRef;
  private ngOnDestroy$ = new Subject<void>();

  private onPositionChange$ = observableFromEvent(window, 'resize').pipe(
    debounceTime(30),
    takeUntil(this.ngOnDestroy$.asObservable()),
  );

  constructor(
    private overlay: Overlay,
    private overlayContainer: OverlayContainer,
  ) {}

  public render(
    componentDef: Type<WalkthroughComponentT>,
    renderOptions: Step['renderOptions'],
    originDirective: WalkthroughStepOrigin,
  ): { componentRef: ComponentRef<WalkthroughComponentT>; overlayRef: OverlayRef } {
    const { elementRef: originRef, beforeRenderCallback: callback } = originDirective;
    this.overlayRef = this.createOverlay(originRef, renderOptions);
    const portal = new ComponentPortal(componentDef);

    this.onPositionChange$.subscribe(() => {
      this.handleResizeEvent();
    });

    if (callback) callback(this);
    this.updatePositionAfterRender();

    const componentRef = this.overlayRef.attach(portal);
    this.updatePosition();

    return { componentRef, overlayRef: this.overlayRef };
  }

  public dispose(callback?: RendererCallbackFn, args?: void | MouseEvent): void {
    this.overlayRef.detach();
    this.overlayRef.dispose();
    this.ngOnDestroy$.next();

    if (callback) callback(args);
  }

  public updatePosition() {
    this.overlayRef.updatePosition();
  }

  private createOverlay(origin: ElementRef, renderOptions: Step['renderOptions']): OverlayRef {
    const { strategy, positions, offset } = renderOptions;

    return this.overlay.create({
      backdropClass: !!renderOptions.useWalkthroughBackdropClass
        ? 'rt-walkthrough-backdrop'
        : 'rt-overlay-dark-backdrop',
      positionStrategy: strategy(this.overlay, origin, positions, offset),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
    });
  }

  private handleResizeEvent() {
    this.overlayRef.updateSize({});
    this.overlayRef.updatePosition();
  }

  private updatePositionAfterRender(): void {
    queueScheduler.schedule(() => {
      this.handleResizeEvent();
    }, 100);
  }
}
